"use client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Head from "next/head";
import { TurnrCard, TurnrCardContent } from "~/components/card/TurnrCard.jsx";
import { TurnrGrid } from "~/components/TurnrGrid.jsx";
import { fetchEntityActions, fetchQualityCheckSearch } from "~/services";
import { useQueryState } from "rylak-common/src/hooks/useQueryState.jsx";
import { useSearchStringState, useTablePageState, useTableSizeState } from "~/hooks";

import { ENTITY_ACTION_PAGE, QUERY_MAP } from "~/constants";
import { TurnrButton } from "~/components/buttons/TurnrButton";
import { faBarcodeRead, faSearch } from "@fortawesome/pro-regular-svg-icons";
import SearchInput from "~/components/input/inputs/SearchInput.jsx";
import TurnrForm from "~/components/input/TurnrForm.jsx";
import PageInformationCard from "~/components/PageInformationCard.jsx";
import { TurnrText } from "~/components/TurnrText.jsx";
import { getQualityCheckPageData, getShopSettingsQualityCheckHref } from "~/cores/pageAccessCore";
import { TurnrActionAlert } from "~/components/alerts/TurnrAlert.jsx";
import TurnrDropdownSelect from "~/components/input/TurnrDropdownSelect.jsx";
import { serviceIsLoading, serviceIsLoadingOrFetched, useServiceHook } from "rylak-common";
import EntityActionsDotsMenu from "~/components/entityAction/EntityActionsDotsMenu.jsx";
import { useAppProps } from "~/app/_apps/AppPropsContext";
import { getTableHeadBrand, TableCellBrand } from "~/components/table/cells/TableCellBrand.jsx";
import { getTableHeadCustomer, TableCellCustomer } from "~/components/table/cells/TableCellCustomer.jsx";
import { useTableSortQuery } from "~/components/table/useTableSortQuery.jsx";
import { getTableHeadOrderId, TableCellOrderId } from "~/components/table/cells/TableCellOrderId.jsx";
import CreateReportView from "./(createReportView)/CreateReportView";
import { getShopOrderCreatedAt } from "~/cores/orderCore";
import { TurnrFontAwesomeIcon } from "~/components/buttons/icon-buttons/TurnrFontAwesomeIconButton";
import { getTableHeadDateTime, TableCellDateTime } from "~/components/table/cells/TableCellDateTime.jsx";
import TurnrTableSmart, { TurnrTableBody, TurnrTableCell, TurnrTableRow, TurnrTableSimple } from "~/components/table/TurnrTableSmart.jsx";
import { TableCellReturnItems } from "~/components/table/cells/TableCellReturnItems.jsx";
import ShipmentOrReturnRow from "./ShipmentOrReturnRow";
import { useHighlightTextState } from "~/components/highlightText/HighlightText.jsx";
import { getTableHeadActions } from "~/components/table/cells/TableCellActions.jsx";
import useSelectListItemModalState from "~/components/modals/useSelectListItemModalState.jsx";
import { getUserQualityCheckReturnStations } from "~/cores/userCore";
import { getLoggedInUser } from "~/cores/appCore";
import { isNilOrEmptyString } from "~/utils";
import TurnrLink from "~/components/links/TurnrLink.jsx";

const useScannedValue = ({ onNewScannedValue }) => {
    const [scannedValue, setScannedValue] = useState("");
    const inputBuffer = useRef(""); // Buffer to collect input
    const lastKeyTime = useRef(0); // Timestamp of the last key event

    useEffect(() => {
        const handleKeyDown = (event) => {
            const currentTime = Date.now();
            const timeDiff = currentTime - lastKeyTime.current;
            lastKeyTime.current = currentTime;

            if (timeDiff > 50) {
                // Reset the buffer if the time between keystrokes is too long
                inputBuffer.current = "";
            }

            // Append the key to the buffer
            if (event.key.length === 1) {
                inputBuffer.current += event.key;
                // event.preventDefault(); // Prevent keypress from affecting any input field
            }

            // Detect the "Enter" key as the end of the scan and that the inputBuffer is at least 5 (probably a scan)
            if (event.key === "Enter" && inputBuffer?.current?.length > 5) {
                setScannedValue(inputBuffer.current);
                onNewScannedValue(inputBuffer.current);
                inputBuffer.current = ""; // Clear the buffer
                event.preventDefault(); // Prevent Enter from affecting any input field
            }
        };

        // Add the event listener
        window.addEventListener("keydown", handleKeyDown);

        // Cleanup the event listener
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [onNewScannedValue]);

    return [scannedValue];
};

function getTableHeadData({ appProps }) {
    return {
        items: [
            getTableHeadBrand({ id: "order.merchantBrand.name", appProps, hide: "xxl" }),
            getTableHeadOrderId({ sortable: false }),
            getTableHeadDateTime({ sortable: false, id: "shopOrderCreatedAt", hide: "lg" }),
            getTableHeadCustomer({ hide: "xl", sortable: false }),
            {
                id: "nrOfReturns",
                label: "Items",
                hide: "lg",
                sortable: false,
            },
            {
                id: "shipments",
                label: "Returns/Shipments",
                hide: "",
                sortable: false,
            },
            getTableHeadActions(),
        ],
    };
}

export default function WarehouseQualityCheckPage() {
    const { appProps } = useAppProps();

    const [searchStringUsedInServiceCall, setSearchStringUsedInServiceCall] = useSearchStringState({ pushToHistory: false }); // When reloading new data we need to make sure we don't accidentally use the search value that might not have been triggered yet
    const [searchStringInInput, setSearchString] = useState(searchStringUsedInServiceCall);
    const [shouldFetchWarehouseOrders, setShouldFetchWarehouseOrders] = useState(!!searchStringUsedInServiceCall);

    const user = getLoggedInUser(appProps);
    const availableUserReturnStations = getUserQualityCheckReturnStations({ user: user });
    const isReturnStationFixed = availableUserReturnStations?.length === 1;
    const [selectedReturnStation, setSelectedReturnStation] = useState(isReturnStationFixed ? availableUserReturnStations?.[0] : null);

    const [reportOnOrderId, setReportOnOrderId] = useQueryState({ key: QUERY_MAP.ORDER_ID, pushToHistory: true });

    const [scanForProductValueModalOpen, scanForProductValue, setScanForProductValue] = useSelectListItemModalState(null);

    const onReportOnChange = useCallback(
        (orderId = "") => {
            setReportOnOrderId(orderId + "");
        },
        [setReportOnOrderId],
    );

    const [page, setPage] = useTablePageState();
    const [size] = useTableSizeState();
    const [successfullyAddedProduct] = useQueryState({
        key: QUERY_MAP.SUCCESSFUL_SERVICE_CALL,
    });

    const tableHeadData = getTableHeadData({ appProps });
    let tableSortData = useTableSortQuery(tableHeadData);

    let { serviceData: warehouseOrdersService, fetchServiceData: fetchWarehouseOrdersFn } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchFn: fetchQualityCheckSearch,
        onReceive: (data) => {
            if (data?.content?.length === 1) {
                const result = data?.content[0];
                const order = result?.matchedOrder;
                onReportOnChange(order?.id);
            }
        },
    });

    const { serviceData: qualityCheckEntityActions, fetchServiceData: fetchQualityCheckEntityActions } = useServiceHook({
        appProps,
        fetchRequested: true,
        fetchData: {
            entityActionPage: ENTITY_ACTION_PAGE.QUALITY_CHECK,
        },
        fetchFn: fetchEntityActions,
    });

    const searchForOrders = useCallback(() => {
        setPage(0);
        setShouldFetchWarehouseOrders(true);
        onGoBackClicked();
    }, [setPage, setShouldFetchWarehouseOrders]);

    const searchOrderInputRef = useRef(null);

    const creatingReport = !serviceIsLoading(warehouseOrdersService) && reportOnOrderId;

    const onNewScannedValue = useCallback(
        (scannedValue) => {
            if (creatingReport && !searchOrderInputRef?.current?.isFocused()) {
                setScanForProductValue(scannedValue);
            } else {
                setSearchString(scannedValue);
                searchForOrders();
            }
        },
        [creatingReport, searchOrderInputRef, searchForOrders],
    );

    useScannedValue({ onNewScannedValue });

    const refetchWarehouseOrders = useCallback(() => {
        if (searchStringInInput) {
            fetchWarehouseOrdersFn(
                {
                    searchString: searchStringInInput.trim(),
                    page,
                    size,
                },
                { silentFetch: true },
            );
            setSearchStringUsedInServiceCall(searchStringInInput);
        }
        setShouldFetchWarehouseOrders(false);
    }, [warehouseOrdersService, searchStringInInput, page, size]);

    useEffect(() => {
        setShouldFetchWarehouseOrders(true);
    }, [page, size]);

    const reloadServices = useCallback(() => {
        fetchQualityCheckEntityActions();
        setShouldFetchWarehouseOrders(true);
        appProps?.triggerFetchBrand();
    }, [setShouldFetchWarehouseOrders]);

    useEffect(() => {
        if (successfullyAddedProduct) {
            appProps?.openSnackbarSuccess({
                title: "Successfully reported products!",
            });
        }
    }, [successfullyAddedProduct]);

    useEffect(() => {
        if (shouldFetchWarehouseOrders) {
            refetchWarehouseOrders();
        }
    }, [shouldFetchWarehouseOrders]);

    const loading = serviceIsLoading(warehouseOrdersService);

    const returnStations = appProps.brandService?.data?.brandData?.settings?.qualityCheckReturnStations;

    function onGoBackClicked() {
        onReportOnChange("", "", "");
    }

    const highlightText = useHighlightTextState({
        searchString: searchStringInInput,
        serviceData: warehouseOrdersService,
    });

    const searchInputLabel = "Tracking reference | Barcode | Order Id";

    const matches = warehouseOrdersService?.data?.content;

    return (
        <>
            <Head>
                <title>Quality check</title>
            </Head>
            <PageInformationCard
                iconProps={{ flip: "horizontal" }}
                icon={getQualityCheckPageData().icon}
                title={"Quality Check"}
                content={
                    <TurnrText size={"sm"}>
                        When you receive a package you search for the Shipment here and submit a report.
                        <br />
                        If you can't find the Shipment it's also possible to report the package on a Return or an Order instead.
                        <br />
                        <br />
                        You can read about and setup return stations and other settings here: {<TurnrLink href={getShopSettingsQualityCheckHref()}>Settings: Quality Check</TurnrLink>}
                    </TurnrText>
                }
            />
            <TurnrGrid container spacing={2}>
                {returnStations?.length > 0 && (
                    <TurnrGrid item xs={12}>
                        <TurnrCard>
                            <TurnrCardContent>
                                <TurnrDropdownSelect
                                    label={isReturnStationFixed ? "Return station" : "Select Return Station"}
                                    value={selectedReturnStation}
                                    options={availableUserReturnStations?.length > 0 ? availableUserReturnStations : returnStations}
                                    onChange={setSelectedReturnStation}
                                    readOnly={isReturnStationFixed}
                                />
                            </TurnrCardContent>
                        </TurnrCard>
                    </TurnrGrid>
                )}

                {(!isNilOrEmptyString(selectedReturnStation) || returnStations?.length === 0 || !returnStations) && (
                    <>
                        <TurnrGrid item xs={12}>
                            <TurnrCard>
                                <TurnrCardContent>
                                    <div
                                        style={{
                                            display: "flex",
                                            width: "100%",
                                            gridGap: "10px",
                                            alignItems: "center",
                                        }}
                                    >
                                        <TurnrForm
                                            style={{
                                                width: "100%",
                                                display: "grid",
                                                gridGap: "10px",
                                                gridTemplateColumns: "repeat( auto-fill, minmax(270px, 1fr) )",
                                                gridAutoColumns: "1fr",
                                                gridAutoRows: "1fr",
                                            }}
                                            onSubmit={searchForOrders}
                                        >
                                            <SearchInput
                                                value={searchStringInInput}
                                                autoFocus={true}
                                                label={searchInputLabel}
                                                disabled={loading}
                                                rightIcon={
                                                    <TurnrFontAwesomeIcon
                                                        icon={faBarcodeRead}
                                                        size={"large"}
                                                        style={{
                                                            marginRight: "5px",
                                                        }}
                                                    />
                                                }
                                                onChange={setSearchString}
                                                debounce={false}
                                                placeholder={""}
                                                ref={searchOrderInputRef}
                                            />
                                            <TurnrButton loading={loading} disabled={!searchStringInInput} fullWidth size="medium" type="submit" variant="contained" fontAwesomeIcon={faSearch}>
                                                Search
                                            </TurnrButton>
                                        </TurnrForm>
                                        <EntityActionsDotsMenu appProps={appProps} onSuccess={reloadServices} entityActions={qualityCheckEntityActions?.data} />
                                    </div>
                                </TurnrCardContent>
                                {serviceIsLoadingOrFetched(warehouseOrdersService) && (
                                    <div
                                        style={{
                                            height: creatingReport && "0px",
                                        }}
                                    >
                                        <TurnrTableSmart
                                            cellSize={"small"}
                                            tableHeadData={tableHeadData}
                                            tableSortData={tableSortData}
                                            page={page}
                                            setPage={setPage}
                                            size={size}
                                            serviceData={warehouseOrdersService}
                                        >
                                            {matches?.map((matchesData) => {
                                                const order = matchesData?.matchedOrder;
                                                const rows = matchesData?.rows || [];
                                                const nrOfRows = rows.length;
                                                const containsReturns = nrOfRows > 0;
                                                return (
                                                    <TurnrTableRow key={order?.id} style={{ height: "50px" }} onClick={() => onReportOnChange(order?.id)}>
                                                        <TableCellBrand appProps={appProps} entity={order} />
                                                        <TableCellOrderId order={order} highlightText={highlightText} />
                                                        <TableCellDateTime dateTime={getShopOrderCreatedAt(order)} />
                                                        <TableCellCustomer order={order} highlightText={highlightText} entityAddress={order?.address} />
                                                        <TableCellReturnItems
                                                            darkerHover={true}
                                                            WrapperComponent={({ children }) => <div style={{ minWidth: "73px" }}>{children}</div>}
                                                            className={"sm-hide"}
                                                            appProps={appProps}
                                                            order={order}
                                                        />
                                                        <TurnrTableCell
                                                            style={{
                                                                maxWidth: "700px",
                                                            }}
                                                            rowClick={true}
                                                            innerStyle={{ width: "auto" }}
                                                        >
                                                            {containsReturns ? (
                                                                <TurnrTableSimple>
                                                                    <TurnrTableBody>
                                                                        {rows?.map(({ orderReturn, shipment }, index) => {
                                                                            return (
                                                                                <ShipmentOrReturnRow
                                                                                    key={order?.id + "_" + orderReturn?.id + "_" + shipment?.id + "_" + index}
                                                                                    appProps={appProps}
                                                                                    order={order}
                                                                                    orderReturn={orderReturn}
                                                                                    shipment={shipment}
                                                                                    highlightText={highlightText}
                                                                                    qualityCheckOrderRows={rows}
                                                                                    onReportOnChange={onReportOnChange}
                                                                                />
                                                                            );
                                                                        })}
                                                                    </TurnrTableBody>
                                                                </TurnrTableSimple>
                                                            ) : undefined}
                                                        </TurnrTableCell>
                                                        <TurnrTableCell tight>
                                                            {/*<div*/}
                                                            {/*    style={{*/}
                                                            {/*        display: "flex",*/}
                                                            {/*        justifyContent: "flex-end",*/}
                                                            {/*    }}*/}
                                                            {/*>*/}
                                                            <TurnrButton size="small" appProps={appProps} onClick={() => onReportOnChange(order?.id)} variant={"text"}>
                                                                Create report
                                                            </TurnrButton>
                                                            {/*</div>*/}
                                                        </TurnrTableCell>
                                                    </TurnrTableRow>
                                                );
                                            })}
                                        </TurnrTableSmart>
                                    </div>
                                )}
                            </TurnrCard>
                        </TurnrGrid>

                        {creatingReport && (
                            <TurnrGrid item xs={12}>
                                <CreateReportView
                                    appProps={appProps}
                                    warehouseOrdersService={warehouseOrdersService}
                                    reportOnOrderId={reportOnOrderId}
                                    onGoBackClicked={onGoBackClicked}
                                    selectedReturnStation={selectedReturnStation}
                                    highlightText={highlightText}
                                    scanForProductValueModalOpen={scanForProductValueModalOpen}
                                    scanForProductValue={scanForProductValue}
                                    setScanForProductValue={setScanForProductValue}
                                />
                            </TurnrGrid>
                        )}

                        {matches?.length === 0 && (
                            <>
                                <TurnrGrid item xs={12}>
                                    <TurnrActionAlert fullWidth>
                                        <TurnrGrid container spacing={2} alignItems={"center"} direction={"row"}>
                                            <TurnrGrid item>No orders found</TurnrGrid>
                                        </TurnrGrid>
                                    </TurnrActionAlert>
                                </TurnrGrid>
                            </>
                        )}
                    </>
                )}
            </TurnrGrid>
        </>
    );
}
